'use client';

import { motion } from 'framer-motion';
import { Filter, Search } from 'lucide-react';
import { useState } from 'react';

import AboutSection from '@/components/AboutSection';
import ContactSection from '@/components/ContactSection';
import Footer from '@/components/Footer';
import Navbar from '@/components/Navbar';
import { themes, useTheme } from '@/contexts/ThemeContext';

import { categories, companies } from '@/components/companies';

export default function PortfolioPage() {
  const { currentTheme } = useTheme();
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');

  const filteredCompanies = companies.filter((company) => {
    const matchesCategory =
      selectedCategory === 'All' || company.category === selectedCategory;
    const matchesSearch =
      company.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      company.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const handleCompanyClick = (website: string) => {
    window.open(website, '_blank', 'noopener,noreferrer');
  };

  return (
    <>
      <Navbar />
      <div
        className="min-h-screen w-full px-4 py-24 sm:px-6 lg:px-8"
        style={{
          background: themes[currentTheme].bgGradient,
          color: themes[currentTheme].text,
        }}
      >
        <div className="mx-auto max-w-7xl">
          {/* Header */}
          <div className="mb-12 text-center">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="font-space-grotesk text-4xl font-bold sm:text-5xl"
            >
              Our Portfolio Companies
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="mt-4 text-lg opacity-80"
            >
              Discover our diverse portfolio of innovative startups
            </motion.p>
          </div>

          {/* Search and Filter */}
          <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 size-5 -translate-y-1/2 opacity-50" />
              <input
                type="text"
                placeholder="Search companies..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full rounded-xl bg-white/10 px-12 py-3 backdrop-blur-lg focus:outline-none focus:ring-2"
                style={{ color: themes[currentTheme].text }}
              />
            </div>
            <div className="flex items-center gap-2 overflow-x-auto">
              <Filter className="size-5 opacity-50" />
              {categories.map((category) => (
                <motion.button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-all ${
                    selectedCategory === category
                      ? 'text-white'
                      : 'hover:bg-white/10'
                  }`}
                  style={
                    selectedCategory === category
                      ? { background: themes[currentTheme].gradient }
                      : undefined
                  }
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {category}
                </motion.button>
              ))}
            </div>
          </div>

          {/* Company Grid */}
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {filteredCompanies.map((company) => (
              <button
                key={company.name}
                type="button"
                onClick={() => handleCompanyClick(company.website)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    handleCompanyClick(company.website);
                  }
                }}
                className="group relative cursor-pointer overflow-hidden rounded-2xl bg-white/10 p-6 text-left backdrop-blur-lg transition-all hover:scale-105 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/20"
                role="link"
                aria-label={`Visit ${company.name}'s website`}
                tabIndex={0}
              >
                <div className="relative z-10">
                  <div className="mb-4 flex items-center gap-4">
                    {company.logo ? (
                      <div className="relative size-12 shrink-0">
                        <img
                          src={company.logo}
                          alt={`${company.name} logo`}
                          className="size-12 object-contain"
                        />
                      </div>
                    ) : (
                      <div className="flex size-12 shrink-0 items-center justify-center rounded-full bg-white/10">
                        <span className="text-lg font-bold">
                          {company.name.charAt(0)}
                        </span>
                      </div>
                    )}
                    <h3
                      className="font-space-grotesk text-xl font-bold"
                    >
                      {company.name}
                    </h3>
                  </div>
                  <p className="mt-2 text-sm opacity-80">
                    {company.description}
                  </p>
                  <div className="mt-4">
                    <span
                      className="inline-block rounded-full px-3 py-1 text-xs font-medium"
                      style={{
                        background: themes[currentTheme].gradient,
                        color: 'white',
                      }}
                    >
                      {company.category}
                    </span>
                  </div>
                </div>

                {/* Decorative Elements */}
                <div
                  className="absolute -right-4 -top-4 size-24 rounded-full opacity-10 blur-3xl transition-all duration-500 group-hover:opacity-20"
                  style={{ background: themes[currentTheme].gradient }}
                />
              </button>
            ))}
          </div>
        </div>
      </div>
      <AboutSection />
      <ContactSection />
      <Footer />
    </>
  );
}
